import {
  ProjectDetailsPageSettingsPageBudgetSectionFragment,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum
} from 'generated/resources/graphql'

import { Result, UnhandledResult } from 'modules/core/lib/handleMutation'

import { IProjectBudgetValues } from 'modules/project_management/components/forms/ProjectBudgetStrategiesForm'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import { getMinutesFromDecimalHours } from 'modules/project_management/lib/formatHours'

export interface GetBudgetSectionMutationOldParams {
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
  data: IProjectBudgetValues
  getCurrentBudgetType: () => budgetTypesEnum
  createBudgetStrategy: (
    dto: {
      projectId: string
      budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
      plannedCents?: number
      plannedMinutes?: number
      feeAmountCents?: number
      fixedCostCents?: number
      isBillable: boolean
      delegated: boolean
    },
    awaitRefetchQueries?: boolean
  ) => Promise<UnhandledResult<Result<unknown>>>
  updateBudgetStrategy: (
    dto: {
      id: string
      budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
      plannedCents?: number
      plannedMinutes?: number
      feeAmountCents?: number
      fixedCostCents?: number
      isBillable: boolean
    },
    awaitRefetchQueries?: boolean
  ) => Promise<UnhandledResult<Result<unknown>>>
  deleteBudgetStrategy: (params: { id: string }) => Promise<UnhandledResult<Result<unknown>>>
}

function calculateFeeAmountCents(
  feeAmountCents: number | null | undefined,
  isBillable: boolean
): number | undefined {
  if (!isBillable) return undefined
  if (!feeAmountCents) return undefined
  return Math.round(Number(feeAmountCents) * 100)
}

// TODO: Remove this function once we open Phases Profitability
export function getBudgetSectionMutationOld(
  params: GetBudgetSectionMutationOldParams
): Promise<UnhandledResult<Result<unknown>>> {
  const {
    project,
    data,
    getCurrentBudgetType,
    createBudgetStrategy,
    updateBudgetStrategy,
    deleteBudgetStrategy
  } = params

  const currentBudgetType = getCurrentBudgetType()
  const targetBudgetType = data.budgetType

  const currentDelegation = project.budgetStrategy?.delegated ?? false
  const targetDelegation = data.delegated

  let plannedCents: number | undefined
  const parsedPlannedCents = data.plannedCents == null ? undefined : Number(data.plannedCents)
  if (
    parsedPlannedCents === undefined ||
    targetBudgetType === budgetTypesEnum.withoutBudget ||
    !Number.isFinite(parsedPlannedCents) ||
    parsedPlannedCents === 0
  ) {
    plannedCents = undefined
  } else {
    plannedCents = Math.round(parsedPlannedCents * 100)
  }

  let plannedMinutes: number | undefined
  const plannedMinutesNumber = Number(data.plannedMinutes)
  if (
    targetBudgetType !== budgetTypesEnum.totalBudget ||
    plannedMinutesNumber === 0 ||
    !Number.isFinite(plannedMinutesNumber)
  ) {
    plannedMinutes = undefined
  } else {
    plannedMinutes = getMinutesFromDecimalHours(plannedMinutesNumber)
  }

  const billable = project.isBillable ?? false
  const feeAmountCents = calculateFeeAmountCents(data.feeAmountCents, billable)

  // Handle deletion (withoutBudget)
  if (targetBudgetType === budgetTypesEnum.withoutBudget && data.delegated === false) {
    if (!project.budgetStrategy) {
      throw new Error('No budget to delete')
    }
    return deleteBudgetStrategy({ id: project.budgetStrategy.id })
  }

  // Handle updates (same type) - fixed and total budget only
  if (currentBudgetType === targetBudgetType && currentDelegation === targetDelegation) {
    const budgetStrategy = project.budgetStrategy
    if (!budgetStrategy) {
      throw new Error('No budget strategy to update')
    }
    if (
      targetBudgetType !== budgetTypesEnum.projectFixedCost &&
      targetBudgetType !== budgetTypesEnum.totalBudget
    ) {
      throw new Error('Cannot update this budget type')
    }
    const validBudgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum =
      targetBudgetType === budgetTypesEnum.projectFixedCost ? 'project_fixed_cost' : 'total_budget'

    return updateBudgetStrategy(
      {
        id: budgetStrategy.id,
        budgetStrategyType: validBudgetStrategyType,
        plannedCents,
        plannedMinutes,
        feeAmountCents,
        fixedCostCents: undefined,
        isBillable: billable
      },
      true
    )
  }

  // Handle creation/switching between types
  const getBudgetStrategyType = (): ProjectManagementValueObjectsBudgetStrategyTypeEnum => {
    if (targetBudgetType === budgetTypesEnum.projectFixedCost) return 'project_fixed_cost'
    if (targetBudgetType === budgetTypesEnum.totalBudget) return 'total_budget'
    if (targetBudgetType === budgetTypesEnum.withoutBudget) return 'without_budget'
    throw new Error(`Unknown budget type: ${targetBudgetType}`)
  }

  if (
    targetBudgetType !== budgetTypesEnum.projectFixedCost &&
    targetBudgetType !== budgetTypesEnum.totalBudget &&
    data.delegated === false
  ) {
    throw new Error(`Unknown budget type: ${targetBudgetType}`)
  }

  const selectedBudget = getBudgetStrategyType()
  return createBudgetStrategy(
    {
      projectId: project.id,
      budgetStrategyType: selectedBudget,
      plannedCents,
      plannedMinutes,
      feeAmountCents,
      fixedCostCents: undefined,
      isBillable: billable,
      delegated: targetDelegation
    },
    true
  )
}
